/**
 * Copyright © 2026 Arberia Experience. Tutti i diritti riservati.
 * Riproduzione, distribuzione o uso non autorizzato di questo codice
 * protetto da diritto d'autore sono severamente vietati.
 */
import { mockData } from './mockData.js';
import { stateManager } from './state.js';
import { prewarmRouteTiles } from './mapPrefetch.js';
// Firebase imports are now loaded dynamically to reduce initial bundle size

class DataManager {
    constructor() {
        // Initialize with mocks immediately so UI is never empty
        this.partners = [...mockData.partners];
        this.bnbPartners = [...(mockData.bnbPartners || [])];
        this.currentLevels = [];
        this.isInitialized = false;
        this.needsUpdate = false;
    }

    async init() {
        if (this.isInitialized) return;

        try {
            const { db } = await import('./firebase.js');
            const { collection, getDocs, doc, getDoc } = await import('firebase/firestore');

            // 1. Version Check
            try {
                const vSnap = await getDoc(doc(db, "metadata", "version"));
                if (vSnap.exists()) {
                    const cloudVersion = vSnap.data().value || 0;
                    if (mockData.DATA_VERSION > cloudVersion) {
                        console.warn(`Local Data (v${mockData.DATA_VERSION}) > Cloud (v${cloudVersion}). Access Admin to Sync.`);
                        this.needsUpdate = true;
                    }
                }
            } catch (e) { console.warn("Version check failed, proceeding..."); }

            // 2. Sync Partners
            try {
                const partnersSnapshot = await getDocs(collection(db, "partners"));
                if (!partnersSnapshot.empty) {
                    this.partners = partnersSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                    console.log(`Synced ${this.partners.length} partners from cloud.`);
                }
            } catch (e) { console.warn("Partners cloud sync failed, using mocks."); }

            // 3. Sync B&B Partners
            try {
                const bnbSnapshot = await getDocs(collection(db, "bnb_partners"));
                if (!bnbSnapshot.empty) {
                    this.bnbPartners = bnbSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                    console.log(`Synced ${this.bnbPartners.length} B&Bs from cloud.`);
                }
            } catch (e) { console.warn("B&B cloud sync failed, using mocks."); }

            // 4. Shop Data Init
            try {
                const { shopData } = await import('./shopData.js');
                await shopData.init();
            } catch (e) { console.warn("ShopData init failed."); }

        } catch (e) {
            console.error("Critical DataManager sync failed:", e);
        }

        this.isInitialized = true;
        
        // Auto-load levels if session active
        const activeBnB = stateManager.getSelectedBnB();
        if (activeBnB) {
            await this.loadLevelsForBnB(activeBnB);
        }
    }

    async loadLevelsForBnB(bnbId) {
        console.log(`Loading levels for ${bnbId}...`);
        try {
            const { db } = await import('./firebase.js');
            const { collection, getDocs } = await import('firebase/firestore');

            const levelsRef = collection(db, "partners", bnbId, "levels");
            const snapshot = await getDocs(levelsRef);

            if (!snapshot.empty) {
                this.currentLevels = snapshot.docs.map(d => {
                    const data = d.data();
                    const mock = mockData.levels.find(l => l.id === parseInt(d.id)) || {};
                    return { ...mock, ...data };
                });
            } else {
                // Fallback to mock route
                const partner = this.partners.find(p => p.id === bnbId);
                if (partner && partner.route) {
                    this.currentLevels = mockData.levels.filter(l => partner.route.includes(l.id));
                }
            }
        } catch (e) {
            const partner = this.partners.find(p => p.id === bnbId);
            if (partner && partner.route) {
                this.currentLevels = mockData.levels.filter(l => partner.route.includes(l.id));
            }
        }

        // Proactively prewarm map tiles in the background
        if (this.currentLevels && this.currentLevels.length > 0) {
            prewarmRouteTiles(this.currentLevels).catch(() => {});
        }
    }

    // ADMIN TOOL: Run this from console: await window.dataManager.seedDatabase()
    async seedDatabase() {
        if (!confirm("⚠️ RE-SEEDING: This will structure Levels inside Partners. Proceed?")) return;

        console.log("Starting Subcollection Seed...");
        const { db } = await import('./firebase.js');
        const { doc, writeBatch } = await import('firebase/firestore');

        const batch = writeBatch(db);

        // 1. Seed Partners
        mockData.partners.forEach(p => {
            const ref = doc(db, "partners", p.id);
            batch.set(ref, p);
        });

        // 2. Seed Levels INTO Partner Subcollections
        // We find which levels belong to which partner using the 'route' array
        mockData.partners.forEach(partner => {
            const partnerLevels = mockData.levels.filter(l => partner.route.includes(l.id));

            partnerLevels.forEach(l => {
                // Path: partners/{partnerId}/levels/{levelId}
                const ref = doc(db, "partners", partner.id, "levels", l.id.toString());
                batch.set(ref, l);
            });
        });


        // 3. Seed B&B Partners (Separate Collection)
        if (mockData.bnbPartners) {
            mockData.bnbPartners.forEach(p => {
                const ref = doc(db, "bnb_partners", p.id);
                batch.set(ref, p);
            });
        }

        // 4. Seed Stories and ShopData
        console.log("Preparing to seed Stories and ShopData...");
        try {
            const { shopData } = await import('./shopData.js');
            const { storyService } = await import('./stories.js');
            
            const stories = await storyService.getAll();
            stories.forEach(s => {
                const ref = doc(db, "stories", s.id);
                batch.set(ref, s);
            });
            
            const collectionsToSeed = ['borghi', 'ristori', 'esperienze', 'menu', 'categorie'];
            collectionsToSeed.forEach(colName => {
                if (shopData[colName]) {
                    shopData[colName].forEach(item => {
                        const ref = doc(db, `shopData_${colName}`, item.id);
                        batch.set(ref, item);
                    });
                }
            });
        } catch(e) {
            console.error("Error staging shop/stories to batch", e);
        }

        await batch.commit();
        console.log("Database Re-Seeded with Subcollections! Version:", mockData.DATA_VERSION);

        // 4. Save Version
        const batches = writeBatch(db);
        const metaRef = doc(db, "metadata", "version");
        batches.set(metaRef, { value: mockData.DATA_VERSION, timestamp: new Date().toISOString() });
        await batches.commit();

        alert("Database Re-Seeded! Refresh App.");
        window.location.reload();
    }

    getLevels() {
        const selectedBnBId = stateManager.getSelectedBnB();
        const partner = this.partners.find(p => p.id === selectedBnBId);

        // Dynamic Injection for "Base" (Level 1) if it exists in route
        // We check currentLevels for ID 1
        const baseLevel = this.currentLevels.find(l => l.id === 1);
        if (baseLevel && partner) {
            if (!baseLevel.coords || baseLevel.coords.lat === 0) baseLevel.coords = partner.coords;
            if (!baseLevel.title) baseLevel.title = `La tua Base: ${partner.name}`;
            if (!baseLevel.description) baseLevel.description = partner.description;
        }

        return this.currentLevels;
    }

    getLevel(id) {
        return this.currentLevels.find(l => l.id === id);
    }

    getPartners() {
        return this.partners.map(p => ({
            ...p,
            isPilot: isPilotPartner(p)
        }));
    }

    getBnbPartners() {
        return this.bnbPartners;
    }

    isLevelInRoute(levelId) {
        return this.currentLevels.some(l => l.id === levelId);
    }

    // --- GUEST CODE MANAGEMENT ---
    async getGuestCodes(userId = null) {
        try {
            const { db } = await import('./firebase.js');
            const { collection, getDocs, query, where, orderBy } = await import('firebase/firestore');
            
            let q;
            if (userId) {
                // Fetch only codes created by this user
                q = query(collection(db, "guestCodes"), where("createdBy", "==", userId));
            } else {
                // Admin or global view
                q = query(collection(db, "guestCodes"), orderBy("createdAt", "desc"));
            }
            
            const snapshot = await getDocs(q);
            const list = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            
            // Sort in memory by createdAt descending
            return list.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        } catch (e) {
            console.error("Error fetching guest codes:", e);
            return [];
        }
    }

    async generateGuestCode(customPrefix = "ARB", creator = null) {
        try {
            const { db } = await import('./firebase.js');
            const { doc, setDoc } = await import('firebase/firestore');
            
            const cleanPrefix = customPrefix.trim().toUpperCase().replace(/[^A-Z0-9]/g, '') || "ARB";
            const randomSuffix = Math.random().toString(36).substr(2, 4).toUpperCase();
            const newCode = `${cleanPrefix}-${randomSuffix}`;
            
            const createdByUid = (creator && typeof creator === 'object') ? (creator.uid || creator.id) : (creator || 'admin');
            const creatorEmail = (creator && typeof creator === 'object') ? (creator.email || null) : null;
            const creatorName = (creator && typeof creator === 'object') ? (creator.displayName || creator.name || null) : null;

            await setDoc(doc(db, "guestCodes", newCode), {
                code: newCode,
                createdAt: new Date().toISOString(),
                createdBy: createdByUid,
                creatorEmail: creatorEmail,
                creatorName: creatorName,
                active: true
            });
            
            return newCode;
        } catch (e) {
            console.error("Error generating guest code:", e);
            throw e;
        }
    }

    async deleteGuestCode(codeId, userId = null) {
        try {
            const { db } = await import('./firebase.js');
            const { doc, deleteDoc, getDoc } = await import('firebase/firestore');
            
            const codeRef = doc(db, "guestCodes", codeId);
            if (userId) {
                const snap = await getDoc(codeRef);
                if (snap.exists()) {
                    const data = snap.data();
                    if (data.createdBy && data.createdBy !== userId && data.createdBy !== 'admin') {
                        throw new Error("Non hai i permessi per eliminare questo codice ospite.");
                    }
                }
            }

            await deleteDoc(codeRef);
            return true;
        } catch (e) {
            console.error("Error deleting guest code:", e);
            throw e;
        }
    }

    // --- VISITOR PHOTOS MANAGEMENT ---
    async getVisitorPhotos() {
        try {
            const { db } = await import('./firebase.js');
            const { collection, getDocs, query, orderBy } = await import('firebase/firestore');
            const q = query(collection(db, "visitor_photos"), orderBy("createdAt", "desc"));
            const snapshot = await getDocs(q);
            const allPhotos = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            return allPhotos.filter(p => p.isPublic !== false);
        } catch (e) {
            console.error("Error fetching visitor photos:", e);
            return [];
        }
    }

    async uploadVisitorPhoto(file, metadata, onProgress) {
        return new Promise(async (resolve, reject) => {
            try {
                const { db, storage } = await import('./firebase.js');
                const { ref, uploadBytesResumable, getDownloadURL } = await import('firebase/storage');
                const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');
                
                const fileExt = file.name.split('.').pop() || 'jpg';
                const uniqueFilename = `visitors/${Date.now()}_${Math.random().toString(36).substring(2, 6)}.${fileExt}`;
                const storageRef = ref(storage, uniqueFilename);
                
                const uploadTask = uploadBytesResumable(storageRef, file);
                
                uploadTask.on('state_changed', 
                    (snapshot) => {
                        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                        if(onProgress) onProgress(progress);
                    }, 
                    (error) => {
                        console.error('Upload failed', error);
                        reject(error);
                    }, 
                    async () => {
                        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                        const docRef = await addDoc(collection(db, "visitor_photos"), {
                            imageUrl: downloadURL,
                            visitorName: metadata.visitorName || 'Visitatore',
                            location: metadata.location || 'Arberia',
                            description: metadata.description || '',
                            isPublic: metadata.isPublic !== undefined ? metadata.isPublic : true,
                            monumentName: metadata.monumentName || metadata.location || 'Sconosciuto',
                            gps: metadata.gps || null,
                            lightingCondition: metadata.lightingCondition || 'Sconosciuto',
                            resolution: metadata.resolution || 'Sconosciuta',
                            createdAt: serverTimestamp(),
                            active: true // utile se in futuro si vuole implementare moderazione
                        });
                        resolve({ id: docRef.id, url: downloadURL });
                    }
                );
            } catch (e) {
                console.error("Error initiating upload:", e);
                reject(e);
            }
        });
    }
}

export const dataManager = new DataManager();
// Expose for Console Debugging/Seeding
window.dataManager = dataManager;

export function isPilotPartner(partner) {
    if (!partner) return false;
    if (partner.isPilot === true) return true;
    const id = (partner.id || '').toLowerCase().trim();
    const name = (partner.name || '').toLowerCase().trim();
    const town = (partner.targetTown || '').toLowerCase().trim();
    
    const pilotKeywords = [
        'badhesa', 'villa badessa',
        'cifti', 'çifti', 'civita',
        'ferma', 'firmo',
        'firmoza', 'firmosa', 'acquaformosa',
        'frasnita', 'frascineto',
        'gharrafa', 'gharaffa', 'garafa', 'caraffa',
        'vina', 'maida', 'vena di maida',
        'platani', 'pllatani', 'pllatëni', 'plataci',
        'mungraxana', 'mungrasana', 'mongrassano',
        'hora', 'piana degli albanesi',
        'shen pali', 'shenpali', 'shën pali', 'san paolo albanese',
        'shen benditi', 'shen benditti', 'shenbenditi', 'shën benedhiti', 'san benedetto ullano',
        'mbuzati', 'san giorgio albanese',
        'shen sofia', 'shensofia', 'shën sofia', 'santa sofia',
        'strighari', 'strigari', 'san cosmo albanese',
        'ungra', 'lungro'
    ];
    
    return pilotKeywords.some(kw => id.includes(kw) || name.includes(kw) || town.includes(kw));
}

